// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { Fleet } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { CosmosDBManagementClient } from "../cosmosDBManagementClient.js";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import { createHttpPoller } from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import type {
  FleetResource,
  FleetListNextOptionalParams,
  FleetListOptionalParams,
  FleetListResponse,
  FleetListByResourceGroupNextOptionalParams,
  FleetListByResourceGroupOptionalParams,
  FleetListByResourceGroupResponse,
  FleetGetOptionalParams,
  FleetGetResponse,
  FleetCreateOptionalParams,
  FleetCreateResponse,
  FleetUpdateOptionalParams,
  FleetUpdateResponse,
  FleetDeleteOptionalParams,
  FleetDeleteResponse,
  FleetListNextResponse,
  FleetListByResourceGroupNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing Fleet operations. */
export class FleetImpl implements Fleet {
  private readonly client: CosmosDBManagementClient;

  /**
   * Initialize a new instance of the class Fleet class.
   * @param client Reference to the service client
   */
  constructor(client: CosmosDBManagementClient) {
    this.client = client;
  }

  /**
   * Lists all the fleets under the subscription.
   * @param options The options parameters.
   */
  public list(options?: FleetListOptionalParams): PagedAsyncIterableIterator<FleetResource> {
    const iter = this.listPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(options, settings);
      },
    };
  }

  private async *listPagingPage(
    options?: FleetListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<FleetResource[]> {
    let result: FleetListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(continuationToken, options);
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    options?: FleetListOptionalParams,
  ): AsyncIterableIterator<FleetResource> {
    for await (const page of this.listPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Lists all the fleets under the specified subscription and resource group.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param options The options parameters.
   */
  public listByResourceGroup(
    resourceGroupName: string,
    options?: FleetListByResourceGroupOptionalParams,
  ): PagedAsyncIterableIterator<FleetResource> {
    const iter = this.listByResourceGroupPagingAll(resourceGroupName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listByResourceGroupPagingPage(resourceGroupName, options, settings);
      },
    };
  }

  private async *listByResourceGroupPagingPage(
    resourceGroupName: string,
    options?: FleetListByResourceGroupOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<FleetResource[]> {
    let result: FleetListByResourceGroupResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listByResourceGroup(resourceGroupName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listByResourceGroupNext(resourceGroupName, continuationToken, options);
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listByResourceGroupPagingAll(
    resourceGroupName: string,
    options?: FleetListByResourceGroupOptionalParams,
  ): AsyncIterableIterator<FleetResource> {
    for await (const page of this.listByResourceGroupPagingPage(resourceGroupName, options)) {
      yield* page;
    }
  }

  /**
   * Lists all the fleets under the subscription.
   * @param options The options parameters.
   */
  private _list(options?: FleetListOptionalParams): Promise<FleetListResponse> {
    return this.client.sendOperationRequest({ options }, listOperationSpec);
  }

  /**
   * Lists all the fleets under the specified subscription and resource group.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param options The options parameters.
   */
  private _listByResourceGroup(
    resourceGroupName: string,
    options?: FleetListByResourceGroupOptionalParams,
  ): Promise<FleetListByResourceGroupResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, options },
      listByResourceGroupOperationSpec,
    );
  }

  /**
   * Retrieves the properties of an existing Azure Cosmos DB fleet under a subscription
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    fleetName: string,
    options?: FleetGetOptionalParams,
  ): Promise<FleetGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, fleetName, options },
      getOperationSpec,
    );
  }

  /**
   * Creates an Azure Cosmos DB fleet under a subscription.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param body The parameters to provide for the current fleet.
   * @param options The options parameters.
   */
  create(
    resourceGroupName: string,
    fleetName: string,
    body: FleetResource,
    options?: FleetCreateOptionalParams,
  ): Promise<FleetCreateResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, fleetName, body, options },
      createOperationSpec,
    );
  }

  /**
   * Updates the properties of an existing Azure Cosmos DB Fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param options The options parameters.
   */
  update(
    resourceGroupName: string,
    fleetName: string,
    options?: FleetUpdateOptionalParams,
  ): Promise<FleetUpdateResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, fleetName, options },
      updateOperationSpec,
    );
  }

  /**
   * Deletes an existing Azure Cosmos DB Fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param options The options parameters.
   */
  async beginDelete(
    resourceGroupName: string,
    fleetName: string,
    options?: FleetDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<FleetDeleteResponse>, FleetDeleteResponse>> {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<FleetDeleteResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, fleetName, options },
      spec: deleteOperationSpec,
    });
    const poller = await createHttpPoller<FleetDeleteResponse, OperationState<FleetDeleteResponse>>(
      lro,
      {
        restoreFrom: options?.resumeFrom,
        intervalInMs: options?.updateIntervalInMs,
        resourceLocationConfig: "azure-async-operation",
      },
    );
    await poller.poll();
    return poller;
  }

  /**
   * Deletes an existing Azure Cosmos DB Fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param options The options parameters.
   */
  async beginDeleteAndWait(
    resourceGroupName: string,
    fleetName: string,
    options?: FleetDeleteOptionalParams,
  ): Promise<FleetDeleteResponse> {
    const poller = await this.beginDelete(resourceGroupName, fleetName, options);
    return poller.pollUntilDone();
  }

  /**
   * ListNext
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    nextLink: string,
    options?: FleetListNextOptionalParams,
  ): Promise<FleetListNextResponse> {
    return this.client.sendOperationRequest({ nextLink, options }, listNextOperationSpec);
  }

  /**
   * ListByResourceGroupNext
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param nextLink The nextLink from the previous successful call to the ListByResourceGroup method.
   * @param options The options parameters.
   */
  private _listByResourceGroupNext(
    resourceGroupName: string,
    nextLink: string,
    options?: FleetListByResourceGroupNextOptionalParams,
  ): Promise<FleetListByResourceGroupNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, nextLink, options },
      listByResourceGroupNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.DocumentDB/fleets",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.FleetListResult,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.$host, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer,
};
const listByResourceGroupOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.DocumentDB/fleets",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.FleetListResult,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.$host, Parameters.subscriptionId, Parameters.resourceGroupName],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.DocumentDB/fleets/{fleetName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.FleetResource,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.fleetName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.DocumentDB/fleets/{fleetName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.FleetResource,
    },
    201: {
      bodyMapper: Mappers.FleetResource,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  requestBody: Parameters.body3,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.fleetName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const updateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.DocumentDB/fleets/{fleetName}",
  httpMethod: "PATCH",
  responses: {
    200: {
      bodyMapper: Mappers.FleetResource,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  requestBody: Parameters.body4,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.fleetName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const deleteOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.DocumentDB/fleets/{fleetName}",
  httpMethod: "DELETE",
  responses: {
    200: {
      headersMapper: Mappers.FleetDeleteHeaders,
    },
    201: {
      headersMapper: Mappers.FleetDeleteHeaders,
    },
    202: {
      headersMapper: Mappers.FleetDeleteHeaders,
    },
    204: {
      headersMapper: Mappers.FleetDeleteHeaders,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.fleetName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.FleetListResult,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  urlParameters: [Parameters.$host, Parameters.subscriptionId, Parameters.nextLink],
  headerParameters: [Parameters.accept],
  serializer,
};
const listByResourceGroupNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.FleetListResult,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
